import {
  Recycle, ShieldCheck, ServerCog, RefreshCw, Store, Truck, Wrench, Leaf, Globe,
} from "lucide-react";

export const SERVICES = [
  {
    slug: "it-asset-disposition",
    n: "01",
    icon: Recycle,
    title: "IT Asset Disposition (ITAD)",
    tag: "End-to-end",
    short: "Secure, compliant, value-driven IT asset retirement — collection, auditing, data sanitization, refurbishment, remarketing and responsible recycling.",
    intro:
      "Our flagship service manages the complete retirement of your IT estate. From secure collection to final recycling, every asset is audited, sanitized and routed for maximum value recovery under a single, accountable process.",
    image:
      "https://images.pexels.com/photos/37730212/pexels-photo-37730212.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    points: [
      "Secure collection & full asset auditing",
      "Certified data sanitization",
      "Refurbishment & global remarketing",
      "Responsible, zero-landfill recycling",
    ],
  },
  {
    slug: "secure-data-destruction",
    n: "02",
    icon: ShieldCheck,
    title: "Secure Data Destruction",
    tag: "NIST 800-88",
    short: "Certified data destruction using NIST SP 800-88 processes — erasure, HDD/SSD shredding, media destruction and Certificates of Data Destruction.",
    intro:
      "Every drive is a liability until verifiably sanitized. We eliminate that risk with NIST SP 800-88 compliant erasure and physical destruction, backed by a Certificate of Data Destruction for every asset.",
    image:
      "https://images.unsplash.com/photo-1518770660439-4636190af475?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxtb3RoZXJib2FyZCUyMGNpcmN1aXQlMjBib2FyZCUyMGUtd2FzdGV8ZW58MHx8fHwxNzg0NTM3MTg4fDA&ixlib=rb-4.1.0&q=85",
    points: [
      "NIST SP 800-88 data erasure",
      "HDD / SSD shredding & media destruction",
      "Per-asset Certificate of Data Destruction",
      "Full serialized chain of custody",
    ],
  },
  {
    slug: "data-center-decommissioning",
    n: "03",
    icon: ServerCog,
    title: "Data Center Decommissioning",
    tag: "Turnkey",
    short: "End-to-end decommissioning of servers, storage, networking, racks and cabling with secure removal, logistics, asset recovery and certified disposal.",
    intro:
      "From a single cabinet to an entire hall, our engineers manage the full decommission — de-rack, cable removal, secure logistics, asset recovery and certified disposal — with zero disruption to live infrastructure.",
    image:
      "https://images.pexels.com/photos/37730212/pexels-photo-37730212.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    points: [
      "Servers, storage, networking, racks & cabling",
      "Secure removal & GPS-tracked logistics",
      "Asset recovery & remarketing",
      "Certified disposal with documentation",
    ],
  },
  {
    slug: "technology-lifecycle-management",
    n: "04",
    icon: RefreshCw,
    title: "Technology Lifecycle Management",
    tag: "Lifecycle",
    short: "Comprehensive management of IT assets from deployment to end-of-life — optimizing performance, reducing costs and achieving sustainability goals.",
    intro:
      "We manage IT assets across their entire lifecycle — from deployment through refresh to end-of-life — helping organizations optimize performance, control costs and hit sustainability targets.",
    image:
      "https://images.pexels.com/photos/4487383/pexels-photo-4487383.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    points: [
      "Deployment to end-of-life management",
      "Performance optimization",
      "Cost reduction & asset visibility",
      "Sustainability reporting",
    ],
  },
  {
    slug: "it-asset-buyback",
    n: "05",
    icon: Store,
    title: "IT Asset Buyback & Value Recovery",
    tag: "Value Recovery",
    short: "Maximize the value of retired IT equipment through competitive buyback programs, refurbishment, global remarketing and responsible asset recovery.",
    intro:
      "End-of-life doesn't mean end-of-value. Our competitive buyback programs, refurbishment and global remarketing channel return maximum recovery on your retired equipment.",
    image:
      "https://images.pexels.com/photos/4487383/pexels-photo-4487383.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    points: [
      "Competitive buyback programs",
      "Refurbishment & grading",
      "Global remarketing channel",
      "Transparent value recovery",
    ],
  },
  {
    slug: "reverse-logistics",
    n: "06",
    icon: Truck,
    title: "Reverse Logistics & Asset Recovery",
    tag: "Global Network",
    short: "Secure global collection, transportation, warehousing, inventory management and asset recovery through our trusted international logistics network.",
    intro:
      "Chain of custody starts the moment assets move. Our international logistics network provides secure collection, transportation, warehousing and inventory management anywhere in the world.",
    image:
      "https://images.pexels.com/photos/4487383/pexels-photo-4487383.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    points: [
      "Secure global collection & transport",
      "Warehousing & inventory management",
      "International logistics network",
      "Serialized asset recovery",
    ],
  },
  {
    slug: "device-refurbishment",
    n: "07",
    icon: Wrench,
    title: "Device Refurbishment & Redeployment",
    tag: "Circular",
    short: "Professional testing, repair, upgrading and redeployment of IT equipment to extend asset life and support circular economy initiatives.",
    intro:
      "We extend the useful life of your hardware through professional testing, repair, upgrading and redeployment — the most sustainable and cost-effective outcome for functional IT.",
    image:
      "https://images.unsplash.com/photo-1518770660439-4636190af475?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxtb3RoZXJib2FyZCUyMGNpcmN1aXQlMjBib2FyZCUyMGUtd2FzdGV8ZW58MHx8fHwxNzg0NTM3MTg4fDA&ixlib=rb-4.1.0&q=85",
    points: [
      "Professional testing & repair",
      "Component upgrading",
      "Redeployment programs",
      "Circular-economy lifecycle extension",
    ],
  },
  {
    slug: "e-waste-recycling",
    n: "08",
    icon: Leaf,
    title: "Sustainable E-Waste Recycling",
    tag: "Zero-Landfill",
    short: "Environmentally responsible recycling of obsolete IT equipment with safe material recovery and full compliance with global environmental standards.",
    intro:
      "Nothing goes to landfill. Obsolete equipment is dismantled and routed through a compliant downstream, recovering materials safely under global environmental standards — with full impact reporting.",
    image:
      "https://images.unsplash.com/photo-1518770660439-4636190af475?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxtb3RoZXJib2FyZCUyMGNpcmN1aXQlMjBib2FyZCUyMGUtd2FzdGV8ZW58MHx8fHwxNzg0NTM3MTg4fDA&ixlib=rb-4.1.0&q=85",
    points: [
      "Environmentally responsible recycling",
      "Safe material recovery",
      "Global environmental compliance",
      "Full impact reporting",
    ],
  },
  {
    slug: "global-program-management",
    n: "09",
    icon: Globe,
    title: "Global Program Management",
    tag: "70+ Countries",
    short: "Centralized management of multinational ITAD and infrastructure projects across 70+ countries — consistent execution and reporting via a single point of contact.",
    intro:
      "One partner, global reach. We centrally manage multinational ITAD and infrastructure projects across 70+ countries, ensuring consistent execution, governance and reporting through a single point of contact.",
    image:
      "https://images.pexels.com/photos/37730212/pexels-photo-37730212.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    points: [
      "Multinational project management",
      "Coverage across 70+ countries",
      "Consistent governance & reporting",
      "Single point of contact",
    ],
  },
];

export const getService = (slug) => SERVICES.find((s) => s.slug === slug);
